import { useId } from 'react';
import type { TrendMixItem } from '@dru/types';
import { ErrorState, cn } from '@dru/ui';
import { CHROME, colorOf, SERIES } from './palette';
import { Swatch } from './Marker';
import { arcPath, donutSegments, formatCount } from './scale';

/**
 * A part-to-whole of at most four segments, with the whole in the middle
 * and every segment named, counted and given its share in the list beside
 * it — the list is the reading; the ring is the glance.
 *
 * Segments are cut from each other with a 2px line of the surface rather
 * than a border, so they stay the series' own colour to the edge.
 */
export function DonutChart({ items, total, unit, loading, error, onRetry, className }: {
  items: TrendMixItem[] | null | undefined;
  total: number;
  /** What one unit is: "companies" or "examinations". */
  unit: string;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
}) {
  const id = useId();
  if (error) return <div className={className}><ErrorState title="The mix could not be loaded" detail={error} onRetry={onRetry} /></div>;
  if (loading && !items) {
    return (
      <div className={cn('flex items-center gap-6', className)} aria-busy="true" aria-label="Loading the mix">
        <div className="h-36 w-36 shrink-0 animate-pulse rounded-full bg-neutral-100" />
        <div className="flex-1 space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-4 animate-pulse rounded bg-neutral-100" />)}</div>
      </div>
    );
  }

  const list = items ?? [];
  const segments = donutSegments(list, (it) => it.count);
  const S = 168;
  const c = S / 2;
  const r = 76;
  const ri = 50;
  const summary = segments.length
    ? segments.map((s) => `${s.item.label} ${formatCount(s.item.count)} (${pct(s.share)})`).join(', ')
    : `No ${unit} in the last 12 months`;

  return (
    <div className={cn('flex flex-wrap items-center gap-x-6 gap-y-4', className)}>
      <svg
        width={S} height={S} viewBox={`0 0 ${S} ${S}`} role="img" aria-labelledby={`${id}-t`}
        className="shrink-0"
      >
        <title id={`${id}-t`}>{unit === 'companies' ? 'Companies by the service they mostly ran' : 'Examinations by service'}. {summary}.</title>
        {segments.length === 0 ? (
          <circle cx={c} cy={c} r={(r + ri) / 2} fill="none" stroke={CHROME.grid} strokeWidth={r - ri} />
        ) : (
          <>
            {/* The ring sweeps in clockwise from twelve: a mask whose stroke
                draws itself, revealing the segments under it. */}
            <defs>
              <mask id={`${id}-sweep`}>
                <circle cx={c} cy={c} r={(r + ri) / 2} fill="none" stroke="white" strokeWidth={r - ri + 8}
                  pathLength={1} transform={`rotate(-90 ${c} ${c})`} className="chart-draw" />
              </mask>
            </defs>
            <g mask={`url(#${id}-sweep)`}>
              {segments.map((s) => (
                <path
                  key={s.item.key} d={arcPath(c, c, r, ri, s.start, s.end)}
                  fill={colorOf(s.item.key)} stroke={CHROME.surface} strokeWidth={segments.length > 1 ? 2 : 0} strokeLinejoin="round"
                >
                  <title>{`${s.item.label}: ${formatCount(s.item.count)} ${unit} (${pct(s.share)})`}</title>
                </path>
              ))}
            </g>
          </>
        )}
        <g className="chart-fade">
          <text x={c} y={c + 2} textAnchor="middle" fontSize="24" fontWeight={700} fill={CHROME.strong}>{formatCount(total)}</text>
          <text x={c} y={c + 20} textAnchor="middle" fontSize="11" fill={CHROME.tick}>{unit}</text>
        </g>
      </svg>

      <ul className="min-w-[180px] flex-1 space-y-1.5 text-small" aria-label={`${unit} by service`}>
        {list.map((it) => {
          const share = total > 0 ? it.count / total : 0;
          const series = SERIES.find((s) => s.key === it.key);
          return (
            <li key={it.key} className="flex items-center gap-2">
              <Swatch shape={series?.marker} color={colorOf(it.key)} area={!series} />
              <span className="min-w-0 flex-1 truncate text-neutral-800">{it.label}</span>
              <span className="tabular-nums font-medium text-neutral-900">{formatCount(it.count)}</span>
              <span className="w-10 text-right tabular-nums text-neutral-500">{total > 0 ? pct(share) : '—'}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function pct(share: number): string {
  return `${Math.round(share * 100)}%`;
}
